import { format, isValid } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

/**
 * Locales
 * @type {Record<string, typeof fr | typeof enUS>}
 */
const locales = { fr, enUS };

/**
 * Format a date
 * @param {Date | string | number | null | undefined} date - Date to format
 * @param {string} formatPattern - Format pattern to use
 * @param {string} locale - Locale to use
 * @param {string} fallbackValue - Value to return if date is invalid
 * @returns {string} Formatted date or fallback value
 */
function formatDate(date, formatPattern = 'dd/MM/yyyy', locale = 'fr', fallbackValue = '--') {
  if (date === null || date === undefined || !isValid(new Date(date))) {
    return fallbackValue;
  }
  return format(new Date(date), formatPattern, { locale: locales[locale] });
}

/**
 * Format time value to HH:mm format (removes seconds if present)
 * @param {string} value - Time value (can be HH:mm:ss or HH:mm format)
 * @returns {string} Formatted time in HH:mm format or original value if invalid
 */
export function formatTime(value) {
  if (!value || typeof value !== 'string') return value;

  // Remove leading/trailing whitespace
  const trimmed = value.trim();
  if (!trimmed) return value;

  // Check if value contains seconds (HH:mm:ss format)
  const timeWithSecondsRegex = /^(\d{1,2}):(\d{2}):(\d{2})$/;
  const match = trimmed.match(timeWithSecondsRegex);

  if (match) {
    // Extract hours and minutes, return in HH:mm format
    const hours = match[1].padStart(2, '0');
    const minutes = match[2];
    return `${hours}:${minutes}`;
  }

  // If already in HH:mm format or invalid format, return as is
  return trimmed;
}

export default formatDate;
